import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { X, Loader2, Search, Check, Upload } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
// manual tab switch used instead of Tabs component for layout control
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ImageUploadDropzone } from '@/components/Images/ImageUploadDropzone'
import {
  useInfiniteImagesList,
  useBulkUploadImages,
  useAssignImagesToLine,
  useLineItemImages,
  type BulkUploadItem,
} from '@/hooks/useImages'
import { cn } from '@/utils/cn'
import type { ImageRead } from '@/types/image'
import { getImage } from '@/api/images'
import { useThumbnailTrackingStore } from '@/stores/useThumbnailTrackingStore'
import { compressImages } from '@/utils/imageCompression'
import { ReorderableImageGallery } from './ReorderableImageGallery'

interface ManageLineImagesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  lineId: string
}

interface PendingUploadFile {
  id: string
  file: File
  name: string
  previewUrl: string
}

type RightTab = 'upload' | 'gallery'

export function ManageLineImagesModal({
  open,
  onOpenChange,
  planId,
  versionId,
  lineId,
}: ManageLineImagesModalProps) {
  const { data: currentImages } = useLineItemImages(planId, versionId, lineId)
  const assignedCount = currentImages?.length ?? 0
  const [activeTab, setActiveTab] = useState<RightTab>('upload')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[960px] h-[80vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle>Manage Images</DialogTitle>
        </DialogHeader>

        {/* Two-panel body */}
        <div className="flex flex-1 min-h-0 overflow-hidden border-t border-border">
          {/* Left panel — Assigned images */}
          <div className="w-[320px] shrink-0 flex flex-col border-r border-border">
            <div className="px-4 py-3.5 shrink-0 flex items-center justify-between border-b border-border bg-muted/30">
              <h3 className="text-sm font-medium text-foreground">Assigned</h3>
              {assignedCount > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {assignedCount} image{assignedCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex-1 min-h-0">
              <ScrollArea className="h-full">
                <div className="p-3">
                  <ReorderableImageGallery
                    planId={planId}
                    versionId={versionId}
                    lineId={lineId}
                  />
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Right panel — Add images */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Tab triggers */}
            <div className="px-5 pt-3 pb-0 shrink-0 flex gap-1 border-b border-border">
              {(['upload', 'gallery'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'px-3 py-2 text-sm font-medium transition-colors cursor-pointer relative',
                    activeTab === tab
                      ? 'text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-foreground'
                      : 'text-foreground/60 hover:text-foreground'
                  )}
                >
                  {tab === 'upload' ? 'Upload New' : 'From Gallery'}
                </button>
              ))}
            </div>

            {/* Tab content — takes all remaining height */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {activeTab === 'upload' ? (
                <UploadTab
                  planId={planId}
                  versionId={versionId}
                  lineId={lineId}
                />
              ) : (
                <GalleryTab
                  planId={planId}
                  versionId={versionId}
                  lineId={lineId}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border shrink-0">
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Upload Tab ────────────────────────────────────────────────

function UploadTab({
  planId,
  versionId,
  lineId,
}: {
  planId: string
  versionId: string
  lineId: string
}) {
  const [pendingFiles, setPendingFiles] = useState<PendingUploadFile[]>([])
  const [compressProgress, setCompressProgress] = useState<{
    done: number
    total: number
  } | null>(null)
  const bulkUpload = useBulkUploadImages()
  const assignImages = useAssignImagesToLine(planId, versionId, lineId)
  const isUploading =
    bulkUpload.isPending || assignImages.isPending || compressProgress !== null

  const handleFilesSelected = useCallback((files: File[]) => {
    const newFiles: PendingUploadFile[] = files.map(file => ({
      id: crypto.randomUUID(),
      file,
      name: file.name.replace(/\.[^.]+$/, ''),
      previewUrl: URL.createObjectURL(file),
    }))
    setPendingFiles(prev => [...prev, ...newFiles].slice(0, 20))
  }, [])

  const removeFile = useCallback((id: string) => {
    setPendingFiles(prev => {
      const removed = prev.find(f => f.id === id)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return prev.filter(f => f.id !== id)
    })
  }, [])

  const updateFileName = useCallback((id: string, name: string) => {
    setPendingFiles(prev => prev.map(f => (f.id === id ? { ...f, name } : f)))
  }, [])

  const addThumbnailEntry = useThumbnailTrackingStore(s => s.addEntry)

  const handleUploadAndAssign = useCallback(async () => {
    setCompressProgress({ done: 0, total: pendingFiles.length })
    const compressed = await compressImages(
      pendingFiles.map(f => f.file),
      (done, total) => setCompressProgress({ done, total })
    )
    setCompressProgress(null)

    const items: BulkUploadItem[] = pendingFiles.map((f, i) => ({
      file: compressed[i],
      name: f.name || f.file.name,
    }))

    const uploaded = await bulkUpload.mutateAsync(items)
    const imageIds = uploaded.map(img => img.id)
    const assigned = await assignImages.mutateAsync(imageIds)

    // Track thumbnail generation for the new default image
    const newDefault = assigned.find(a => a.is_default)
    if (newDefault) {
      const uploadedImage = uploaded.find(u => u.id === newDefault.image_id)
      if (
        uploadedImage?.thumbnail_status === 'PENDING' &&
        uploadedImage?.events_url
      ) {
        addThumbnailEntry({
          lineId,
          planId,
          versionId,
          eventsUrl: uploadedImage.events_url,
        })
      }
    }

    setPendingFiles([])
  }, [
    pendingFiles,
    bulkUpload,
    assignImages,
    lineId,
    planId,
    versionId,
    addThumbnailEntry,
  ])

  const canUpload = useMemo(
    () => pendingFiles.length > 0 && pendingFiles.every(f => f.name.trim()),
    [pendingFiles]
  )

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Scrollable content */}
      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full">
          <div className="px-5 py-4 space-y-3">
            <ImageUploadDropzone
              onFilesSelected={handleFilesSelected}
              maxFiles={20 - pendingFiles.length}
              disabled={isUploading || pendingFiles.length >= 20}
            />

            {pendingFiles.length > 0 && (
              <div className="space-y-2">
                {pendingFiles.map(pf => (
                  <div
                    key={pf.id}
                    className="flex items-center gap-3 rounded-lg border border-border p-2"
                  >
                    <img
                      src={pf.previewUrl}
                      alt={pf.name}
                      className="h-10 w-10 rounded object-cover shrink-0"
                    />
                    <Input
                      value={pf.name}
                      onChange={e => updateFileName(pf.id, e.target.value)}
                      placeholder="Image name"
                      className="h-8 text-sm flex-1"
                      disabled={isUploading}
                    />
                    <button
                      onClick={() => removeFile(pf.id)}
                      className="shrink-0 cursor-pointer"
                      disabled={isUploading}
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Sticky action bar — pinned bottom */}
      {pendingFiles.length > 0 && (
        <div className="px-5 py-3 border-t border-border bg-white shrink-0 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {pendingFiles.length} file{pendingFiles.length !== 1 ? 's' : ''}{' '}
            ready
          </span>
          <Button
            size="sm"
            onClick={handleUploadAndAssign}
            disabled={!canUpload || isUploading}
            className="cursor-pointer"
          >
            {compressProgress ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Compressing {compressProgress.done}/{compressProgress.total}...
              </>
            ) : isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload & Assign ({pendingFiles.length})
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── Gallery Tab ───────────────────────────────────────────────

function GalleryTab({
  planId,
  versionId,
  lineId,
}: {
  planId: string
  versionId: string
  lineId: string
}) {
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteImagesList(search)
  const assignImages = useAssignImagesToLine(planId, versionId, lineId)
  const { data: currentImages } = useLineItemImages(planId, versionId, lineId)

  const images = data?.pages.flatMap(page => page.data) ?? []
  const assignedImageIds = new Set(
    (currentImages ?? []).map(img => img.image_id)
  )

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    const root = scrollContainerRef.current
    if (!sentinel || !root) return

    // ScrollArea viewport is the actual scrolling element
    const container =
      root.querySelector('[data-slot="scroll-area-viewport"]') ?? root

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { root: container, rootMargin: '200px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const toggleSelect = useCallback((imageId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(imageId)) next.delete(imageId)
      else next.add(imageId)
      return next
    })
  }, [])

  const addThumbnailEntry = useThumbnailTrackingStore(s => s.addEntry)

  const handleAssign = useCallback(async () => {
    const assigned = await assignImages.mutateAsync(Array.from(selectedIds))

    // Track thumbnail generation if the new default has no thumbnail yet
    const newDefault = assigned.find(a => a.is_default)
    if (newDefault && !newDefault.thumbnail_url) {
      try {
        const imageDetail = await getImage(newDefault.image_id)
        if (
          imageDetail.thumbnail_status === 'PENDING' &&
          imageDetail.events_url
        ) {
          addThumbnailEntry({
            lineId,
            planId,
            versionId,
            eventsUrl: imageDetail.events_url,
          })
        }
      } catch {
        // Ignore — fallback to polling via query invalidation
      }
    }

    setSelectedIds(new Set())
  }, [selectedIds, assignImages, lineId, planId, versionId, addThumbnailEntry])

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Search — pinned top */}
      <div className="px-5 pt-4 pb-3 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search images by name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Scrollable gallery grid */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0">
        <ScrollArea className="h-full">
          <div className="px-5 pb-2">
            {isLoading ? (
              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-lg bg-muted animate-pulse"
                  />
                ))}
              </div>
            ) : images.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-muted-foreground">No images found</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {images.map((image: ImageRead) => {
                  const isSelected = selectedIds.has(image.id)
                  const isAlreadyAssigned = assignedImageIds.has(image.id)
                  const src = image.thumbnail_url || image.download_url
                  return (
                    <button
                      key={image.id}
                      onClick={() =>
                        !isAlreadyAssigned && toggleSelect(image.id)
                      }
                      disabled={isAlreadyAssigned}
                      className={cn(
                        'relative aspect-square rounded-lg overflow-hidden border-2 transition-all',
                        isAlreadyAssigned
                          ? 'border-border opacity-50 cursor-not-allowed'
                          : isSelected
                            ? 'border-[#CF1F2E] ring-2 ring-[#CF1F2E]/20 cursor-pointer'
                            : 'border-border hover:border-[#CF1F2E]/50 cursor-pointer'
                      )}
                    >
                      {src ? (
                        <img
                          src={src}
                          alt={image.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-muted text-xs text-muted-foreground">
                          No preview
                        </div>
                      )}

                      {/* Already assigned overlay */}
                      {isAlreadyAssigned && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <span className="text-[10px] font-semibold text-white bg-black/50 px-1.5 py-0.5 rounded">
                            Assigned
                          </span>
                        </div>
                      )}

                      {/* Selection checkmark */}
                      {isSelected && !isAlreadyAssigned && (
                        <div className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#CF1F2E]">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}

                      {/* Name overlay */}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                        <p className="text-[10px] text-white truncate">
                          {image.name}
                        </p>
                      </div>
                    </button>
                  )
                })}

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="col-span-4 h-1" />
              </div>
            )}

            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Sticky action bar — pinned bottom */}
      {selectedIds.size > 0 && (
        <div className="px-5 py-3 border-t border-border bg-white shrink-0 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {selectedIds.size} selected
          </span>
          <Button
            size="sm"
            onClick={handleAssign}
            disabled={assignImages.isPending}
            className="cursor-pointer"
          >
            {assignImages.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              `Add Selected (${selectedIds.size})`
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
